import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Yup from '../../../../utils/yupExtensions';

import { useGetEducationLevelsHook } from '../../../../utils/utilityHooks';

import FormField from '../../../UI/FormUI/FormField/FormField';
import FormSelectField from '../../../UI/FormUI/FormSelectField/FormSelectField';
import FormDateField from '../../../UI/FormUI/FormDateField/FormDateField';
import FormFileField from '../../../UI/FormUI/FormFileField/FormFileField';
import Button from '../../../UI/Buttons/Button/Button';

import styles from './CVAndCoverLetter.module.css';

function CVAndCoverLetter({ onSubmit, user, ...props }) {
    const { t } = useTranslation();

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

    const createValidationSchema = useCallback((education_level) => {
        return Yup.object({
            cv_file: Yup.mixed()
                .required(t('forms.education.required'))
                .test('fileSize', t('forms.errors.file_too_large'), value => {
                    if (!value) return true;
                    return value.size <= MAX_FILE_SIZE;
                }),
            cv_ref_letter: Yup.mixed()
                .test('fileSize', t('forms.errors.file_too_large'), value => {
                    if (!value) return true;
                    return value.size <= MAX_FILE_SIZE;
                }),
            video_cv: Yup.mixed()
                .test('fileSize', t('forms.errors.file_too_large'), value => {
                    if (!value) return true;
                    return value.size <= MAX_FILE_SIZE;
                }),
        });
    }, [t]);

    const [validationSchema, setValidationSchema] = useState(createValidationSchema(''));

    const formik = useFormik({
        initialValues: {
            cv_file: '',
            cv_ref_letter: '',
            video_cv: '',
        },
        validationSchema: validationSchema,
        onSubmit: values => {
            onSubmit({
                ...values,
            });
        },
    });

    const educationLevels = useGetEducationLevelsHook();

    // Update the validation schema dynamically based on the `education_level` field
    useEffect(() => {
        setValidationSchema(createValidationSchema(formik.values.education_level));
    }, [formik.values.education_level, createValidationSchema]);

    return (
        <form onSubmit={formik.handleSubmit} {...props}>
            <div className={styles.fileSizeNote}>
                {t('forms.file_size_note', 'Note: Maximum file size allowed is 50MB')}
            </div>
            <div>
                <div>
                    <FormFileField
                        name="cv_file"
                        type="file"
                        label={t('edit_profile.job_seeker.cv_file')}
                        hasBorder
                        accept=".pdf,.doc,.docx,.odt"
                        onChange={(event) => {
                            const file = event.target.value;
                            if (file && file.size <= MAX_FILE_SIZE) {
                                formik.setFieldValue('cv_file', file);
                            } else {
                                formik.setFieldError('cv_file', t('forms.errors.file_too_large'));
                            }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.cv_file}
                        error={formik.touched.cv_file && formik.errors.cv_file}
                    />
                </div>
                <div>
                    <FormFileField
                        name="cv_ref_letter"
                        type="file"
                        label={t('edit_profile.job_seeker.cv_ref_letterOptional')}
                        hasBorder
                        accept=".pdf,.doc,.docx,.odt"
                        onChange={(event) => {
                            const file = event.target.value;
                            if (file && file.size <= MAX_FILE_SIZE) {
                                formik.setFieldValue('cv_ref_letter', file);
                            } else {
                                formik.setFieldError('cv_ref_letter', t('forms.errors.file_too_large'));
                            }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.cv_ref_letter}
                        error={formik.touched.cv_ref_letter && formik.errors.cv_ref_letter}
                    />
                </div>
                <div>
                    <FormFileField
                        name="video_cv"
                        type="file"
                        label={t('edit_profile.job_seeker.video_cv')}
                        hasBorder
                        accept=".mp4,.mov,.avi,.wmv"
                        onChange={(event) => {
                            const file = event.target.value;
                            if (file && file.size <= MAX_FILE_SIZE) {
                                formik.setFieldValue('video_cv', file);
                            } else {
                                formik.setFieldError('video_cv', t('forms.errors.file_too_large'));
                            }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.video_cv}
                        error={formik.touched.video_cv && formik.errors.video_cv}
                    />
                </div>
            </div>
            <div>
                <Button type="submit" className={styles.button}>{t('forms.education.add')}</Button>
            </div>
        </form>
    );
}

export default CVAndCoverLetter;